import { IUserDTO } from '../services/user.service';
import { Role } from 'app/shared/models/role';
import { AuthenticationProviders, getAuthProviderNameAsString } from 'app/shared/enums/authentication-providers';
import { KeyEmpty, Key, Entity } from '@kpd/framework-ngx';
import { UMLocalizationService } from '../services/localization.service';

interface IApplication {
  name: string;
  roles: Role[];
}

interface ITenant {
  name: string;
  description: string;
  id: string;
}

export interface ITenantInfo {
  tenant: ITenant;
  isDefault: boolean;
  applications: IApplication[];
  scopes: string[];
}

export class User extends Entity {
  key: Key<string>;
  password?: any;
  confirmPassword?: any;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  culture?: string;
  isEnabled?: boolean;
  upn?: string;
  tenants?: ITenantInfo[];
  displayName?: string;
  allowedAuthenticationProviders?: AuthenticationProviders;
  language?: string;
  lastLoginDate?: Date;
  isSystemUser: boolean;
  isLockedOut: boolean;

  constructor(dto?: IUserDTO) {
    super(dto);
    if (dto && dto.allowedAuthenticationProviders) {
      this.allowedAuthenticationProviders =
        AuthenticationProviders[
          dto.allowedAuthenticationProviders.charAt(0).toLocaleLowerCase() +
            dto.allowedAuthenticationProviders.substring(1)
        ];
    }
    const culture = Object.keys(UMLocalizationService.getCultures())
      .map((cultureKey) => ({
        name: cultureKey,
        display: UMLocalizationService.getCultures()[cultureKey],
      }))
      .find((c) => c.name === this.culture);
    if (culture) {
      this.language = culture.display;
    }
  }

  static composeDisplayName(firstName?: string, lastName?: string): string {
    firstName = (firstName || '').trim();
    lastName = (lastName || '').trim();

    return `${firstName}${firstName && lastName ? ' ' : ''}${lastName}`;
  }

  getDisplayName(): string {
    if (this.displayName) {
      return this.displayName;
    }
    return User.composeDisplayName(this.firstName, this.lastName);
  }

  getApplicationsWithRoles(activeTenantId: string): IApplication[] {
    if (!this.tenants) {
      return [];
    }
    const applications: IApplication[] = [];
    this.tenants
      .filter((t) => t.tenant.id === activeTenantId)
      .forEach((tenant) => {
        applications.push(...tenant.applications);
      });

    return applications;
  }

  getRolesCount(activeTenantId: string): number {
    let roleCount = 0;
    if (!this.tenants) {
      return 0;
    }
    this.tenants
      .filter((t) => t.tenant.id === activeTenantId)
      .forEach((tenant) => {
        tenant.applications.forEach((app) => {
          roleCount += app.roles.length;
        });
      });
    return roleCount;
  }

  setKey(dto: any): void {
    this.key = new Key<string>(dto.upn);
  }

  isLocal(): boolean {
    return this.allowedAuthenticationProviders === 1;
  }

  clone(): User {
    const clone = new User();
    Object.assign(clone, this);
    return clone;
  }

  isNew(): boolean {
    return !this.key || this.key instanceof KeyEmpty;
  }

  isDefaultTenant(tenantId: string): boolean {
    if (!this.tenants) {
      return false;
    }
    return this.tenants.some((tenantInfo: ITenantInfo) => tenantInfo.isDefault && tenantInfo.tenant.id === tenantId);
  }

  getDetailUrl(): string {
    return `/users/detail/${this.upn}`;
  }

  get type(): string {
    return getAuthProviderNameAsString(this.allowedAuthenticationProviders as number);
  }

  get typeAsArray(): number[] {
    const type = this.allowedAuthenticationProviders as number;
    return type <= 2 ? [type] : [1, 2];
  }
}
